<template>
  <svg
    class="vue-trend"
    :width="width"
    :height="height"
    :viewBox="`0 0 ${width} ${height}`"
    preserveAspectRatio="none"
  >
    <defs v-if="gradientStops.length">
      <linearGradient
        :id="gradientId"
        x1="0%"
        y1="0%"
        x2="100%"
        y2="0%"
      >
        <stop
          v-for="(stop, index) in gradientStops"
          :key="index"
          :offset="stop.offset"
          :stop-color="stop.color"
        />
      </linearGradient>
    </defs>
    <path
      fill="none"
      :stroke="strokePaint"
      :stroke-width="strokeWidth"
      stroke-linecap="round"
      stroke-linejoin="round"
      :d="pathD"
    />
  </svg>
</template>

<script>
let trendId = 0

export default {
  name: 'Trend',
  props: {
    data: {
      type: Array,
      default: () => []
    },
    gradient: {
      type: Array,
      default: () => []
    },
    width: {
      type: [Number, String],
      default: 100
    },
    height: {
      type: [Number, String],
      default: 30
    },
    stroke: {
      type: String,
      default: '#c9302c'
    },
    strokeWidth: {
      type: [Number, String],
      default: 2
    },
    padding: {
      type: [Number, String],
      default: 4
    },
    smooth: {
      type: Boolean,
      default: false
    },
    autoDraw: {
      type: Boolean,
      default: false
    },
    autoDrawDuration: {
      type: [Number, String],
      default: 500
    }
  },
  data () {
    return {
      gradientId: `vue-trend-gradient-${++trendId}`
    }
  },
  computed: {
    values () {
      return (this.data || []).map(item => {
        if (typeof item === 'number') return item
        if (item && typeof item.value === 'number') return item.value
        const n = Number(item && item.value)
        return Number.isFinite(n) ? n : 0
      })
    },
    coords () {
      const vals = this.values
      if (!vals.length) return []
      const w = Number(this.width) || 100
      const h = Number(this.height) || 30
      const pad = Number(this.padding) || 0
      const min = Math.min(...vals)
      const max = Math.max(...vals)
      const range = max - min || 1
      return vals.map((v, i) => ({
        x: pad + (i / Math.max(vals.length - 1, 1)) * (w - pad * 2),
        y: h - pad - ((v - min) / range) * (h - pad * 2)
      }))
    },
    pathD () {
      const pts = this.coords
      if (!pts.length) return ''
      if (!this.smooth || pts.length < 3) {
        return pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' ')
      }
      // Simple Catmull-Rom → cubic bezier for a soft sparkline.
      let d = `M${pts[0].x} ${pts[0].y}`
      for (let i = 0; i < pts.length - 1; i++) {
        const p0 = pts[i - 1] || pts[i]
        const p1 = pts[i]
        const p2 = pts[i + 1]
        const p3 = pts[i + 2] || p2
        const cp1x = p1.x + (p2.x - p0.x) / 6
        const cp1y = p1.y + (p2.y - p0.y) / 6
        const cp2x = p2.x - (p3.x - p1.x) / 6
        const cp2y = p2.y - (p3.y - p1.y) / 6
        d += ` C${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`
      }
      return d
    },
    gradientStops () {
      const colors = (this.gradient || []).filter(Boolean)
      if (!colors.length) return []
      if (colors.length === 1) {
        return [{ offset: '0%', color: colors[0] }, { offset: '100%', color: colors[0] }]
      }
      return colors.map((color, index) => ({
        offset: `${(index / (colors.length - 1)) * 100}%`,
        color
      }))
    },
    strokePaint () {
      return this.gradientStops.length ? `url(#${this.gradientId})` : this.stroke
    }
  }
}
</script>
